/**
 * Jobs Help Content
 * 
 * Feature: 136-dashboard-alignment (T053)
 * Pages: /jobs
 * Constitution: v1.12.0
 */

import type { PageHelpContent } from '../../components/ui/PageHelpPanel';

export const jobsHelp: PageHelpContent = {
  pageTitle: 'Jobs & Batch Configuration',
  purpose: 'Configure and monitor batch jobs including collection, backup, restore, and retention tasks.',
  sections: [
    {
      title: 'Job Types',
      concepts: [
        { term: 'Collection', definition: 'Jobs that discover and inventory Fabric resources (workspaces, lakehouses, warehouses) from the tenant.' },
        { term: 'Backup', definition: 'Jobs that create point-in-time snapshots of lakehouse and warehouse data.' },
        { term: 'Restore', definition: 'Jobs that recover data from backup snapshots to a target resource.' },
        { term: 'Retention', definition: 'Cleanup jobs that remove expired backups according to retention policies.' },
      ],
    },
    {
      title: 'Job Lifecycle',
      paragraphs: [
        'Jobs are defined with a schedule and parameters, then executed by the job infrastructure. Each execution creates a Job Run with its own lifecycle.',
      ],
      concepts: [
        { term: 'Job Definition', definition: 'The configuration template that defines what the job does, its schedule, and parameters.' },
        { term: 'Job Run', definition: 'A single execution instance of a job definition. Has its own status, timestamps, and logs.' },
        { term: 'Retry Behavior', definition: 'Failed jobs can be automatically retried based on the retry policy (max retries, backoff strategy).' },
      ],
    },
    {
      title: 'Monitoring',
      paragraphs: [
        'The jobs page shows active, recent, and scheduled job runs. Filter by job type, status, or capacity type. Drill into individual runs for detailed logs and error messages.',
      ],
    },
    {
      title: 'How To',
      steps: [
        { step: 1, instruction: 'Open the Jobs page to view your organization\'s active and recent job runs. The Schedules tab shows when each job runs; the platform sets that schedule.' },
        { step: 2, instruction: 'Use the filter controls to narrow the list by job type (Collection, Backup, Restore, Retention) or status (Running, Failed, Completed).' },
        { step: 3, instruction: 'To run a job now, open Job Definitions and click Run Now. It runs for your organization only, on your worker. The button then shows where the job is, and the same job cannot be sent twice while it is waiting or running. It appears under Recent Runs when it starts.' },
        { step: 4, instruction: 'Review run history by clicking a job definition to see all past executions with timestamps, durations, and outcomes.' },
        { step: 5, instruction: 'Investigate a failed run by opening its detail view to check error messages, logs, and retry options.' },
      ],
    },
    {
      title: 'Related Pages',
      links: [
        { label: 'Backup Runs', href: '/backup' },
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Schedules', href: '/backup/schedules' },
      ],
    },
  ],
};

export default jobsHelp;
